import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../css/profile.css';

const UserProfile = () => {
  const { user, updateUser, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // 프로필 수정 폼 데이터
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    department: user?.department || '',
    position: user?.position || ''
  });
  
  // 비밀번호 변경 폼 데이터
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});

  // 프로필 수정 모드 토글
  const toggleEditMode = () => {
    if (isEditing) {
      // 취소 시 원래 데이터로 복원
      setProfileData({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        department: user?.department || '',
        position: user?.position || ''
      });
      setErrors({});
    }
    setIsEditing(!isEditing);
  };

  // 프로필 입력값 변경
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // 비밀번호 입력값 변경
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // 프로필 유효성 검사
  const validateProfile = () => {
    const newErrors = {};
    
    if (!profileData.name || profileData.name.length < 2) {
      newErrors.name = '이름은 2자 이상이어야 합니다.';
    }
    
    if (!profileData.email || !/\S+@\S+\.\S+/.test(profileData.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다.';
    }
    
    if (profileData.phone && !/^[0-9]{10,11}$/.test(profileData.phone.replace(/-/g, ''))) {
      newErrors.phone = '올바른 전화번호 형식이 아닙니다.';
    }
    
    return newErrors;
  };

  // 비밀번호 유효성 검사
  const validatePassword = () => {
    const newErrors = {};
    
    if (!passwordData.currentPassword) {
      newErrors.currentPassword = '현재 비밀번호를 입력해주세요.';
    }
    
    if (!passwordData.newPassword) {
      newErrors.newPassword = '새 비밀번호를 입력해주세요.';
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = '비밀번호는 8자 이상이어야 합니다.';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)) {
      newErrors.newPassword = '비밀번호는 대소문자와 숫자를 포함해야 합니다.';
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }
    
    return newErrors;
  };

  // 프로필 업데이트
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateProfile();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    const result = await updateUser(profileData);
    
    if (result.success) {
      setMessage({ type: 'success', text: '프로필이 성공적으로 업데이트되었습니다.' });
      setIsEditing(false);
    } else {
      setMessage({ type: 'error', text: result.error });
    }
    
    setLoading(false);
  };

  // 비밀번호 변경
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validatePassword();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
    
    if (result.success) {
      setMessage({ type: 'success', text: '비밀번호가 성공적으로 변경되었습니다.' });
      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } else {
      setMessage({ type: 'error', text: result.error });
    }
    
    setLoading(false);
  };

  // 로그아웃 처리
  const handleLogout = async () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      await logout();
      navigate('/login');
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-wrapper">
        <div className="profile-header">
          <h1>내 프로필</h1>
          <button onClick={handleLogout} className="logout-btn">
            로그아웃
          </button>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="profile-card">
          <div className="profile-section">
            <div className="section-header">
              <h2>기본 정보</h2>
              {!isEditing && (
                <button onClick={toggleEditMode} className="edit-btn">
                  수정
                </button>
              )}
            </div>

            {isEditing ? (
              <form onSubmit={handleProfileSubmit} className="profile-form">
                <div className="form-group">
                  <label>이름</label>
                  <input
                    type="text"
                    name="name"
                    value={profileData.name}
                    onChange={handleProfileChange}
                    className={errors.name ? 'error' : ''}
                    disabled={loading}
                  />
                  {errors.name && <span className="error-text">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label>이메일</label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                    className={errors.email ? 'error' : ''}
                    disabled={loading}
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label>전화번호</label>
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    className={errors.phone ? 'error' : ''}
                    placeholder="010-0000-0000"
                    disabled={loading}
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>

                <div className="form-group">
                  <label>부서</label>
                  <input
                    type="text"
                    name="department"
                    value={profileData.department}
                    onChange={handleProfileChange}
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label>직급</label>
                  <select
                    name="position"
                    value={profileData.position}
                    onChange={handleProfileChange}
                    disabled={loading}
                  >
                    <option value="">선택하세요</option>
                    <option value="staff">사원</option>
                    <option value="senior">대리</option>
                    <option value="manager">과장</option>
                    <option value="deputy">차장</option>
                    <option value="director">부장</option>
                    <option value="executive">임원</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={toggleEditMode} className="cancel-btn" disabled={loading}>
                    취소
                  </button>
                  <button type="submit" className="save-btn" disabled={loading}>
                    {loading ? '저장 중...' : '저장'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="profile-info">
                <div className="info-item">
                  <span className="label">이름:</span>
                  <span className="value">{user?.name}</span>
                </div>
                <div className="info-item">
                  <span className="label">이메일:</span>
                  <span className="value">{user?.email}</span>
                </div>
                <div className="info-item">
                  <span className="label">전화번호:</span>
                  <span className="value">{user?.phone || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="label">부서:</span>
                  <span className="value">{user?.department || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="label">직급:</span>
                  <span className="value">
                    {user?.position ? 
                      {
                        staff: '사원',
                        senior: '대리',
                        manager: '과장',
                        deputy: '차장',
                        director: '부장',
                        executive: '임원'
                      }[user.position] || user.position
                      : '-'
                    }
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">가입일:</span>
                  <span className="value">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="profile-section">
            <div className="section-header">
              <h2>비밀번호 변경</h2>
            </div>

            {isChangingPassword ? (
              <form onSubmit={handlePasswordSubmit} className="password-form">
                <div className="form-group">
                  <label>현재 비밀번호</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className={errors.currentPassword ? 'error' : ''}
                    disabled={loading}
                  />
                  {errors.currentPassword && <span className="error-text">{errors.currentPassword}</span>}
                </div>

                <div className="form-group">
                  <label>새 비밀번호</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className={errors.newPassword ? 'error' : ''}
                    placeholder="8자 이상, 대소문자/숫자 포함"
                    disabled={loading}
                  />
                  {errors.newPassword && <span className="error-text">{errors.newPassword}</span>}
                </div>

                <div className="form-group">
                  <label>새 비밀번호 확인</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className={errors.confirmPassword ? 'error' : ''}
                    disabled={loading}
                  />
                  {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={() => {
                      setIsChangingPassword(false);
                      setPasswordData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                      setErrors({});
                    }} 
                    className="cancel-btn"
                    disabled={loading}
                  >
                    취소
                  </button>
                  <button type="submit" className="save-btn" disabled={loading}>
                    {loading ? '변경 중...' : '비밀번호 변경'}
                  </button>
                </div>
              </form>
            ) : (
              <button 
                onClick={() => setIsChangingPassword(true)} 
                className="change-password-btn"
              >
                비밀번호 변경하기
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;